"use client";

import { classifyAuthOutcomeError } from "@analytics/lib/auth-outcome-analytics";
import { useAuthAnalytics } from "@analytics/lib/use-auth-analytics";
import { zodResolver } from "@hookform/resolvers/zod";
import { authClient } from "@repo/auth/client";
import { Alert, AlertDescription, AlertTitle } from "@repo/ui/components/alert";
import { Button } from "@repo/ui/components/button";
import { TextField } from "@repo/ui/components/field-types";
import { Form, FormField, FormItem } from "@repo/ui/components/form";
import { useAuthErrorMessages } from "@saas/auth/hooks/errors-messages";
import { AlertTriangleIcon, ArrowLeftIcon, MailboxIcon } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import * as z from "zod/v4";

const formSchema = z.object({
	email: z.string().email(),
});

export function ForgotPasswordForm() {
	const t = useTranslations();
	const { authPasswordResetOutcome } = useAuthAnalytics();
	const { getAuthErrorMessage } = useAuthErrorMessages();

	const form = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
		},
	});

	const onSubmit = form.handleSubmit(async ({ email }) => {
		try {
			const redirectTo = new URL(
				"/auth/reset-password",
				window.location.origin,
			).toString();

			const { error } = await authClient.requestPasswordReset({
				email,
				redirectTo,
			});

			if (error) {
				throw error;
			}

			authPasswordResetOutcome({
				result: "verification_sent",
				step: "request",
			});
		} catch (e) {
			authPasswordResetOutcome({
				result: "error",
				step: "request",
				...classifyAuthOutcomeError(e),
			});
			form.setError("root", {
				message: getAuthErrorMessage(
					e && typeof e === "object" && "code" in e
						? (e.code as string)
						: undefined,
				),
			});
		}
	});

	return (
		<>
			<h1 className="font-bold text-xl md:text-2xl">
				{t("auth.forgotPassword.title")}
			</h1>
			<p className="mt-1 mb-6 text-foreground/60">
				{t("auth.forgotPassword.message")}{" "}
			</p>

			{form.formState.isSubmitSuccessful ? (
				<Alert variant="success">
					<MailboxIcon />
					<AlertTitle>
						{t("auth.forgotPassword.hints.linkSent.title")}
					</AlertTitle>
					<AlertDescription>
						{t("auth.forgotPassword.hints.linkSent.message")}
					</AlertDescription>
				</Alert>
			) : (
				<Form {...form}>
					<form
						className="flex flex-col items-stretch gap-4"
						onSubmit={onSubmit}
					>
						{form.formState.errors.root && (
							<Alert variant="error">
								<AlertTriangleIcon />
								<AlertTitle>
									{form.formState.errors.root.message}
								</AlertTitle>
							</Alert>
						)}

						<FormField
							control={form.control}
							name="email"
							render={({ field, fieldState }) => (
								<FormItem>
									<TextField
										id="forgot-password-email"
										label={t("auth.forgotPassword.email")}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										autoComplete="email"
										error={fieldState.error?.message}
									/>
								</FormItem>
							)}
						/>

						<Button loading={form.formState.isSubmitting}>
							{t("auth.forgotPassword.submit")}
						</Button>
					</form>
				</Form>
			)}

			<div className="mt-6 text-center text-sm">
				<Link href="/auth/login">
					<ArrowLeftIcon className="mr-1 inline size-4 align-middle" />
					{t("auth.forgotPassword.backToSignin")}
				</Link>
			</div>
		</>
	);
}
